import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { tenants } from '@/api/client'
import { Building2, ExternalLink, RefreshCw, Plus, Loader2, AlertCircle } from 'lucide-react'
import { formatDate, formatRelative } from '@/lib/utils'

const statusLabel: Record<number, { label: string; cls: string }> = {
  1: { label: 'Aguardando consent', cls: 'badge-warning' },
  2: { label: 'Ativo', cls: 'badge-success' },
  3: { label: 'Falhando', cls: 'badge-error' },
  4: { label: 'Desabilitado', cls: 'badge-neutral' },
}

export function TenantsPage() {
  const queryClient = useQueryClient()
  const [showConsent, setShowConsent] = useState(false)
  const [consentNote, setConsentNote] = useState('')
  const [generatedUrl, setGeneratedUrl] = useState<string | null>(null)

  const { data, isLoading } = useQuery({
    queryKey: ['tenants'],
    queryFn: () => tenants.list(),
    refetchInterval: 30_000,
  })

  const connect = useMutation({
    mutationFn: () => tenants.connectUrl(consentNote || undefined),
    onSuccess: (res) => {
      setGeneratedUrl(res.consentUrl)
    },
  })

  const validate = useMutation({
    mutationFn: (id: string) => tenants.validate(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['tenants'] }),
  })

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Tenants conectados</h1>
          <p className="text-sm text-slate-500 mt-1">Tenants Microsoft 365 disponíveis para migração</p>
        </div>
        <button onClick={() => { setShowConsent(true); setGeneratedUrl(null); setConsentNote('') }} className="btn-primary">
          <Plus className="h-4 w-4" /> Conectar tenant
        </button>
      </div>

      {showConsent && (
        <div className="card p-6 mb-6 border-brand-200 bg-brand-50">
          <h2 className="font-semibold text-slate-900 mb-3">Conectar novo tenant</h2>
          {!generatedUrl ? (
            <>
              <div className="mb-3">
                <label className="label">Nota (opcional)</label>
                <input
                  className="input"
                  value={consentNote}
                  onChange={(e) => setConsentNote(e.target.value)}
                  placeholder="Ex: Tenant cliente XYZ"
                />
              </div>
              <div className="flex gap-2">
                <button onClick={() => connect.mutate()} disabled={connect.isPending} className="btn-primary">
                  {connect.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                  Gerar URL de consent
                </button>
                <button onClick={() => setShowConsent(false)} className="btn-secondary">Cancelar</button>
              </div>
            </>
          ) : (
            <>
              <p className="text-sm text-slate-700 mb-3">
                URL gerada. <strong>Abra em janela anônima</strong> e faça login como Global Admin do tenant que quer conectar:
              </p>
              <div className="bg-white rounded border border-slate-300 p-3 text-xs font-mono break-all mb-3">
                {generatedUrl}
              </div>
              <div className="flex gap-2">
                <a href={generatedUrl} target="_blank" rel="noopener noreferrer" className="btn-primary">
                  <ExternalLink className="h-4 w-4" /> Abrir em nova aba
                </a>
                <button onClick={() => { navigator.clipboard.writeText(generatedUrl) }} className="btn-secondary">
                  Copiar URL
                </button>
                <button onClick={() => setShowConsent(false)} className="btn-secondary">Fechar</button>
              </div>
            </>
          )}
        </div>
      )}

      {isLoading ? (
        <div className="text-slate-500">Carregando...</div>
      ) : !data?.length ? (
        <div className="card p-12 text-center">
          <Building2 className="h-12 w-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">Nenhum tenant conectado ainda</p>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {data.map((t) => (
            <div key={t.id} className="card p-5">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Building2 className="h-5 w-5 text-brand-600" />
                  <div>
                    <div className="font-semibold text-slate-900">{t.organizationDisplayName || t.displayName}</div>
                    <div className="text-xs text-slate-500">{t.primaryDomain}</div>
                  </div>
                </div>
                <span className={statusLabel[t.status]?.cls || 'badge-neutral'}>
                  {statusLabel[t.status]?.label || `?${t.status}`}
                </span>
              </div>

              {t.lastValidationError && (
                <div className="rounded-md bg-red-50 border border-red-200 p-2 mb-3 flex gap-2">
                  <AlertCircle className="h-4 w-4 text-red-600 mt-0.5 shrink-0" />
                  <span className="text-xs text-red-800">{t.lastValidationError}</span>
                </div>
              )}

              <div className="grid grid-cols-2 gap-2 text-xs text-slate-600 mb-4">
                <div>
                  <div className="text-slate-400">Usuários</div>
                  <div className="font-medium text-slate-700">{t.userCount ?? '—'}</div>
                </div>
                <div>
                  <div className="text-slate-400">Conectado em</div>
                  <div className="font-medium text-slate-700">{formatDate(t.consentGrantedAt)}</div>
                </div>
                <div className="col-span-2">
                  <div className="text-slate-400">Última validação</div>
                  <div className="font-medium text-slate-700">{formatRelative(t.lastValidatedAt)}</div>
                </div>
              </div>

              <button
                onClick={() => validate.mutate(t.id)}
                disabled={validate.isPending}
                className="btn-secondary w-full text-xs"
              >
                {validate.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
                Revalidar agora
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
